import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaUserCircle, FaUserPlus, FaEdit, FaTrashAlt, FaTimes, FaEye, FaEyeSlash, FaCropAlt, FaUsers } from 'react-icons/fa';
import { FiArrowLeft } from 'react-icons/fi';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../../config';
import styles from './GerenciarUsuarios.module.css';
import PageHeader from '../../components/PageHeader/PageHeader';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';

function getCroppedImg(image, crop, fileName) {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');
    const pixelRatio = window.devicePixelRatio;
    canvas.width = crop.width * pixelRatio;
    canvas.height = crop.height * pixelRatio;
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY, 0, 0, crop.width, crop.height);
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Canvas is empty'));
                return;
            }
            blob.name = fileName;
            resolve(blob);
        }, 'image/jpeg', 1);
    });
}

const GerenciarUsuarios = () => {
    const [usuarios, setUsuarios] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentUser, setCurrentUser] = useState({
        id: null, username: '', password: '', tipo_usuario: 'padrão',
        nome_completo: '', cpf: '', numero_habilitacao: ''
    });
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [passwordShown, setPasswordShown] = useState(false);
    const [confirmPasswordShown, setConfirmPasswordShown] = useState(false);
    const [imageSrc, setImageSrc] = useState('');
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState(null);
    const [showCropModal, setShowCropModal] = useState(false);
    const [croppedImageBlob, setCroppedImageBlob] = useState(null);
    const imgRef = useRef(null);
    const [previewSource, setPreviewSource] = useState('');
    const fileInputRef = useRef(null);
    const [pageLoading, setPageLoading] = useState(true);

    const getAuthToken = () => {
        const token = localStorage.getItem('token');
        if (!token) {
            setError("Token de autenticação não encontrado.");
            Swal.fire('Erro', 'Token de autenticação não encontrado. Faça login novamente.', 'error');
            return null;
        }
        return `Bearer ${token}`;
    };

    const fetchUsuarios = async () => {
        const token = getAuthToken();
        if (!token) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/usuarios`, {
                headers: { 'Authorization': token }
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Falha ao buscar usuários.');
            }
            const data = await response.json();
            setUsuarios(data);
            setError(''); 
        } catch (error) {
            console.error('Erro:', error);
            setError(error.message || 'Não foi possível carregar os usuários.');
        }
    };

    useEffect(() => {
        const MIN_LOADING_TIME = 300;
        const startTime = Date.now();

        const loadInitialData = async () => {
            await fetchUsuarios(); 

            const endTime = Date.now();
            const duration = endTime - startTime;
            const remainingTime = MIN_LOADING_TIME - duration;

            if (remainingTime > 0) {
                setTimeout(() => setPageLoading(false), remainingTime);
            } else {
                setPageLoading(false);
            }
        };

        loadInitialData();
    }, []);

    const resetModalState = () => {
        setIsEditing(false);
        setCurrentUser({
            id: null, username: '', password: '', tipo_usuario: 'padrão',
            nome_completo: '', cpf: '', numero_habilitacao: ''
        });
        setConfirmPassword('');
        setError('');
        setPasswordShown(false);
        setConfirmPasswordShown(false);
        setPreviewSource('');
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        setCroppedImageBlob(null);
        setImageSrc('');
        setShowCropModal(false);
    };

    const handleOpenModal = (usuario = null) => {
        resetModalState();
        if (usuario) {
            setIsEditing(true);
            setCurrentUser({ ...currentUser, ...usuario, password: '' });
            if (usuario.foto_perfil_url) {
                setPreviewSource(`${API_BASE_URL}${usuario.foto_perfil_url}`);
            }
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        resetModalState();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCurrentUser({ ...currentUser, [name]: value });
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setCrop(undefined);
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                setImageSrc(reader.result?.toString() || '');
                setShowCropModal(true);
            });
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    function onImageLoad(e) {
        const { width, height } = e.currentTarget;
        const crop = centerCrop(makeAspectCrop({ unit: '%', width: 90 }, 1, width, height), width, height);
        setCrop(crop);
    }

    const handleCropSave = async () => {
        if (!completedCrop || !imgRef.current) return;
        const croppedBlob = await getCroppedImg(imgRef.current, completedCrop, 'user-avatar.jpg');
        setCroppedImageBlob(croppedBlob);
        setPreviewSource(URL.createObjectURL(croppedBlob));
        setShowCropModal(false);
        setImageSrc('');
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        const token = getAuthToken();
        if (!token) return;

        if (!isEditing && currentUser.password !== confirmPassword) {
            setError('As senhas não coincidem.');
            return;
        }

        const formData = new FormData();
        Object.keys(currentUser).forEach(key => {
            if (key !== 'id' && key !== 'password') {
                formData.append(key, currentUser[key]);
            }
        });
        
        if (currentUser.password) {
            formData.append('password', currentUser.password);
        }
        if (croppedImageBlob) {
            formData.append('foto_perfil', croppedImageBlob);
        }

        const url = isEditing ? `${API_BASE_URL}/api/usuarios/${currentUser.id}` : `${API_BASE_URL}/api/usuarios`;
        const method = isEditing ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method,
                headers: { 'Authorization': token },
                body: formData,
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Ocorreu um erro.');
            }
            Swal.fire('Sucesso!', `Usuário ${isEditing ? 'atualizado' : 'criado'} com sucesso.`, 'success');
            fetchUsuarios();
            handleCloseModal();
        } catch (error) {
            console.error('Erro ao salvar usuário:', error);
            setError(error.message);
        }
    };

    const handleDeleteClick = (usuario) => {
        Swal.fire({
            title: 'Confirmar Exclusão',
            html: `Tem certeza que deseja excluir o usuário <strong>"${usuario.username}"</strong>?<br/><small>Esta ação não pode ser desfeita.</small>`,
            icon: 'warning',
            iconColor: '#ef4444',
            showCancelButton: true,
            confirmButtonText: 'Sim, Excluir!',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
        }).then(async (result) => {
            if (result.isConfirmed) {
                const token = getAuthToken();
                if (!token) return;

                try {
                    const response = await fetch(`${API_BASE_URL}/api/usuarios/${usuario.id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': token },
                    });

                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.error || 'Falha ao excluir usuário.');
                    }
                    
                    Swal.fire('Excluído!', 'O usuário foi removido com sucesso.', 'success');
                    fetchUsuarios();

                } catch (error) {
                    console.error('Erro ao excluir:', error);
                    Swal.fire('Erro!', error.message || 'Não foi possível excluir o usuário.', 'error');
                }
            }
        });
    };
    
    // Função auxiliar para formatar o nome do tipo de usuário
    const formatUserType = (type) => {
        if (!type) return 'N/A';
        const typeMap = {
            'admin_geral': 'Admin Geral',
            'admin_frota': 'Admin Frota',
            'admin_imovel': 'Admin Imóvel',
            'operador': 'Operador',
            'padrão': 'Padrão'
        };
        // Se o tipo for o antigo 'motorista', ele será tratado como 'operador' no backend.
        // Se houverem dados antigos com "motorista" no frontend, exibiremos "Operador"
        return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
    }


    return (
        <div className={styles.container}>
            {pageLoading && <LoadingOverlay text="Carregando usuários..." />}

            {!pageLoading && (
                <>
                    <PageHeader
                        title="Gerenciamento de Usuários"
                        subtitle="Adicione, edite ou remova usuários do sistema."
                        backTo="/"
                    >
                        <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => handleOpenModal()}>
                            <FaUserPlus /> Novo Usuário
                        </button>
                    </PageHeader>

                    {error && !showModal && (
                    <div className={styles.mainErrorMessage}>{error}</div>
                    )}

                    <main>
                    <div className={styles.userListContainer}>
                        <div className={styles.listHeaderGrid}>
                            <span>Usuário</span>
                            <span className={styles.typeHeader}>Tipo</span>
                            <span className={styles.actionsHeader}>Ações</span>
                        </div>

                        {usuarios.length > 0 ? (
                            <div className={styles.userList}>
                                {usuarios.map((usuario) => (
                                    <div key={usuario.id} className={styles.userCardGrid}>
                                        <div className={styles.userInfoWithAvatar}>
                                            {usuario.foto_perfil_url ? (
                                                <img src={`${API_BASE_URL}${usuario.foto_perfil_url}?${new Date().getTime()}`} alt={usuario.username} className={styles.userAvatar} />
                                            ) : (
                                                <FaUserCircle className={styles.userAvatarPlaceholder} />
                                            )}
                                            <div className={styles.userInfo}>
                                                <span className={styles.username}>{usuario.username}</span>
                                                <span className={styles.userId}>ID: {usuario.id}</span>
                                            </div>
                                        </div>
                                        <div className={styles.userTypeInfo}>
                                            <span className={styles.userTypeLabel} data-type={usuario.tipo_usuario}>
                                                {formatUserType(usuario.tipo_usuario)}
                                            </span>
                                        </div>
                                        <div className={styles.userActions}>
                                            <button className={`${styles.iconBtn} ${styles.editBtn}`} onClick={() => handleOpenModal(usuario)} title="Editar">
                                                <FaEdit />
                                            </button>
                                            <button className={`${styles.iconBtn} ${styles.deleteBtn}`} onClick={() => handleDeleteClick(usuario)} title="Excluir">
                                                <FaTrashAlt />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className={styles.noResults}>
                                <FaUsers className={styles.noResultsIcon} />
                                <h3>Nenhum usuário encontrado</h3>
                                <p>{!error ? 'Comece adicionando um novo usuário no botão acima.' : ''}</p>
                            </div>
                        )}
                    </div>
                    </main>
                    
                    {showModal && (
                        <div className={styles.userModalOverlay} onClick={handleCloseModal}>
                            <div className={styles.userModalContainer} onClick={(e) => e.stopPropagation()}>
                                <header className={styles.userModalHeader}>
                                    <h3>{isEditing ? 'Editar Usuário' : 'Novo Usuário'}</h3>
                                    <button className={styles.userModalCloseBtn} onClick={handleCloseModal}><FaTimes /></button>
                                </header>
                                
                                <div className={styles.userModalBody}>
                                    <form onSubmit={handleSubmit} className={styles.userModalForm} id="user-form">
                                        {error && <div className={`${styles.formGroup} ${styles.formGroupFullWidth} ${styles.errorMessage}`}>{error}</div>}
                                        
                                        <div className={`${styles.formGroup} ${styles.formGroupFullWidth}`}>
                                            <label>Foto de Perfil</label>
                                            <div className={styles.avatarUploadSection}>
                                                <div className={styles.avatarWrapper} onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                                                    {previewSource ? (
                                                        <img src={previewSource} alt="Preview" className={styles.avatarPreview} />
                                                    ) : (
                                                        <FaUserCircle className={styles.avatarPreviewPlaceholder} />
                                                    )}
                                                    <div className={styles.avatarOverlay}>Trocar</div>
                                                </div>
                                                <input
                                                    type="file" accept="image/*" onChange={handleFileChange}
                                                    ref={fileInputRef} className={styles.fileInput} id="file-upload"
                                                />
                                                <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => fileInputRef.current.click()}>
                                                    Selecionar Imagem
                                                </button>
                                            </div>
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label htmlFor="username">Nome de Usuário</label>
                                            <input type="text" id="username" name="username" value={currentUser.username} onChange={handleChange} required />
                                        </div>
                                        
                                        <div className={styles.formGroup}>
                                            <label htmlFor="tipo_usuario">Tipo de Usuário</label>
                                            <select id="tipo_usuario" name="tipo_usuario" value={currentUser.tipo_usuario} onChange={handleChange} required>
                                                {/* --- OPÇÕES ATUALIZADAS --- */}
                                                <option value="padrão">Padrão</option>
                                                <option value="admin_geral">Admin Geral</option>
                                                <option value="admin_frota">Admin Frota</option>
                                                <option value="admin_imovel">Admin Imóvel</option>
                                                <option value="operador">Operador</option>
                                                {/* --- FIM DAS OPÇÕES ATUALIZADAS --- */}
                                            </select>
                                        </div>

                                        {/* --- LÓGICA ATUALIZADA PARA 'operador' --- */}
                                        {currentUser.tipo_usuario === 'operador' && (
                                            <>
                                                <div className={`${styles.formGroup} ${styles.formGroupFullWidth}`}>
                                                    <label htmlFor="nome_completo">Nome Completo do Operador</label>
                                                    <input type="text" id="nome_completo" name="nome_completo" value={currentUser.nome_completo} onChange={handleChange} required={currentUser.tipo_usuario === 'operador'} />
                                                </div>
                                                <div className={styles.formGroup}>
                                                    <label htmlFor="cpf">CPF</label>
                                                    <input type="text" id="cpf" name="cpf" value={currentUser.cpf} onChange={handleChange} required={currentUser.tipo_usuario === 'operador'} />
                                                </div>
                                                <div className={styles.formGroup}>
                                                    <label htmlFor="numero_habilitacao">Nº da Habilitação (CNH)</label>
                                                    <input type="text" id="numero_habilitacao" name="numero_habilitacao" value={currentUser.numero_habilitacao} onChange={handleChange} required={currentUser.tipo_usuario === 'operador'} />
                                                </div>
                                            </>
                                        )}
                                        {/* --- FIM DA LÓGICA ATUALIZADA --- */}

                                        <div className={styles.formGroup}>
                                            <label htmlFor="password">Senha {isEditing && <span>(Deixe em branco para não alterar)</span>}</label>
                                            <div className={styles.passwordWrapper}>
                                                <input type={passwordShown ? 'text' : 'password'} id="password" name="password"
                                                    value={currentUser.password} onChange={handleChange} required={!isEditing}
                                                />
                                                <span className={styles.passwordToggleIcon} onClick={() => setPasswordShown(!passwordShown)}>
                                                    {passwordShown ? <FaEyeSlash /> : <FaEye />}
                                                </span>
                                            </div>
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label htmlFor="confirmPassword">Confirmar Senha</label>
                                            <div className={styles.passwordWrapper}>
                                                <input type={confirmPasswordShown ? 'text' : 'password'} id="confirmPassword" name="confirmPassword"
                                                    value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                                                    required={!isEditing || (isEditing && !!currentUser.password)}
                                                />
                                                <span className={styles.passwordToggleIcon} onClick={() => setConfirmPasswordShown(!confirmPasswordShown)}>
                                                    {confirmPasswordShown ? <FaEyeSlash /> : <FaEye />}
                                                </span>
                                            </div>
                                        </div>
                                    </form>
                                </div>

                                <footer className={styles.userModalFooter}>
                                    <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={handleCloseModal}>Cancelar</button>
                                    <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} form="user-form">Salvar Alterações</button>
                                </footer>
                            </div>
                        </div>
                    )}
                    
                    {showCropModal && (
                        <div className={styles.cropModalOverlay}>
                            <div className={styles.cropModalContent}>
                                <div className={styles.cropModalHeader}>
                                    <h3>Ajustar Foto de Perfil</h3>
                                    <button onClick={() => setShowCropModal(false)} className={styles.closeModalBtn}><FaTimes /></button>
                                </div>
                                <div className={styles.cropContainer}>
                                    {imageSrc && (
                                        <ReactCrop crop={crop} onChange={(_, p) => setCrop(p)} onComplete={(c) => setCompletedCrop(c)} aspect={1} circularCrop>
                                            <img ref={imgRef} alt="Recortar" src={imageSrc} onLoad={onImageLoad} />
                                        </ReactCrop>
                                    )}
                                </div>
                                <div className={styles.cropModalFooter}>
                                    <button onClick={() => setShowCropModal(false)} className={`${styles.btn} ${styles.btnSecondary}`}>Cancelar</button>
                                    <button onClick={handleCropSave} className={`${styles.btn} ${styles.btnPrimary}`}><FaCropAlt /> Aplicar Corte</button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default GerenciarUsuarios;